import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import api from "../../utils/api";
import socket from "../../socket";
import PageHeader from "../../components/common/PageHeader";
import TicketCard from "../../components/Kitchen/TicketCard";
import KitchenSummary, { dishKey } from "../../components/Kitchen/KitchenSummary";
import { playOrderSound, keepScreenAwake } from "../../utils/orderSound";

const COLUMNS = [
  { status: "new", title: "New", accent: "bg-amber-500" },
  { status: "preparing", title: "Preparing", accent: "bg-blue-600" },
  { status: "ready", title: "Ready", accent: "bg-green-600" },
];

const FILTERS = [
  { id: "all", label: "All" },
  { id: "table", label: "Table" },
  { id: "takeaway", label: "Takeaway" },
  { id: "online", label: "Online" },
];

const role = () => localStorage.getItem("role");

const KitchenSettings = ({ settings, onClose, onSaved }) => {
  const [form, setForm] = useState({
    defaultPrepMin: settings?.defaultPrepMin ?? 15,
    parallelTickets: settings?.parallelTickets ?? 3,
  });
  const [saving, setSaving] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.put("/kitchen/settings", form);
      toast.success("Kitchen settings saved");
      onSaved(data);
    } catch (err) {
      toast.error(err.response?.data?.message || "Could not save settings");
    } finally {
      setSaving(false);
    }
  };

  const field = (name, label, hint, max) => (
    <label className="block">
      <span className="block text-sm font-semibold text-gray-800">{label}</span>
      <input
        type="number"
        min="1"
        max={max}
        required
        value={form[name]}
        onChange={(e) => setForm((f) => ({ ...f, [name]: e.target.value }))}
        className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
      />
      <span className="block text-xs text-gray-500 mt-1">{hint}</span>
    </label>
  );

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <form
        onSubmit={save}
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-xl shadow-xl w-full max-w-md p-5 space-y-4"
      >
        <h2 className="text-lg font-bold text-gray-900">Kitchen settings</h2>
        {field(
          "defaultPrepMin",
          "Default cooking time (minutes)",
          "Used for dishes with no prep time set on the product or its category, until the kitchen has learned the real time.",
          240,
        )}
        {field(
          "parallelTickets",
          "Orders cooked at the same time",
          "How many tickets the kitchen can work on at once. Waiting times are worked out from this.",
          50,
        )}
        <div className="flex justify-end gap-2 pt-1">
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg border border-gray-300 font-semibold">
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            className="px-4 py-2 rounded-lg bg-blue-600 text-white font-semibold disabled:opacity-50"
          >
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </form>
    </div>
  );
};

const KitchenPage = () => {
  const [view, setView] = useState("active"); // active | done
  const [filter, setFilter] = useState("all");
  const [mobileColumn, setMobileColumn] = useState("new");
  const [selectedDish, setSelectedDish] = useState(null); // dish summary tap: only tickets with it
  const [data, setData] = useState({ tickets: [], settings: null, today: null });
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const [now, setNow] = useState(Date.now());
  const clockOffset = useRef(0); // server time - this device's time
  const viewRef = useRef(view);
  viewRef.current = view;

  const isAdmin = ["admin", "superAdmin"].includes(role());

  const load = useCallback(async () => {
    try {
      const { data: res } = await api.get("/kitchen/tickets", {
        params: viewRef.current === "done" ? { scope: "done" } : {},
      });
      clockOffset.current = new Date(res.now).getTime() - Date.now();
      setData(res);
    } catch (err) {
      toast.error(err.response?.data?.message || "Could not load kitchen orders");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    setLoading(true);
    load();
  }, [view, load]);

  // Live updates
  useEffect(() => {
    let timer = null;
    const onUpdate = (event = {}) => {
      // The kitchen user gets the new-order sound here; admins already
      // hear one from the order alerts
      if (event.type === "new" && role() === "kitchen") {
        playOrderSound();
        toast.info(`🍳 New ticket – ${event.label}`, { position: "top-right", autoClose: 4000 });
      }
      clearTimeout(timer);
      timer = setTimeout(load, 300);
    };
    // Missed events while offline: reload on reconnect
    const onConnect = () => load();
    socket.on("kitchen-updated", onUpdate);
    socket.on("connect", onConnect);
    return () => {
      clearTimeout(timer);
      socket.off("kitchen-updated", onUpdate);
      socket.off("connect", onConnect);
    };
  }, [load]);

  // Timers tick every 15s
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 15000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => keepScreenAwake(), []);

  const serverNow = now + clockOffset.current;

  const replaceTicket = (ticket) =>
    setData((d) => ({ ...d, tickets: d.tickets.map((t) => (t._id === ticket._id ? ticket : t)) }));

  const run = async (ticket, request) => {
    setBusyId(ticket._id);
    try {
      const { data: updated } = await request();
      replaceTicket(updated);
    } catch (err) {
      toast.error(err.response?.data?.message || "Could not update the ticket");
      load();
    } finally {
      setBusyId(null);
    }
  };

  const changeStatus = (ticket, status) =>
    run(ticket, () => api.put(`/kitchen/tickets/${ticket._id}/status`, { status }));

  const toggleItem = (ticket, item, done) =>
    run(ticket, () => api.put(`/kitchen/tickets/${ticket._id}/items/${item._id}`, { done }));

  const bySource = useMemo(
    () => data.tickets.filter((t) => filter === "all" || t.source === filter),
    [data.tickets, filter],
  );
  const filtered = useMemo(
    () =>
      selectedDish && view === "active"
        ? bySource.filter((t) =>
            t.items.some((i) => i.status !== "cancelled" && dishKey(i) === selectedDish),
          )
        : bySource,
    [bySource, selectedDish, view],
  );
  const selectedDishName = selectedDish
    ? bySource.flatMap((t) => t.items).find((i) => dishKey(i) === selectedDish)?.name
    : null;
  const byStatus = (status) => filtered.filter((t) => t.status === status);
  const countFor = (source) =>
    data.tickets.filter((t) => source === "all" || t.source === source).length;

  const card = (ticket) => (
    <TicketCard
      key={ticket._id}
      ticket={ticket}
      now={serverNow}
      busy={busyId === ticket._id}
      onStatus={changeStatus}
      onToggleItem={toggleItem}
    />
  );

  const subtitle = data.today
    ? `${data.today.finished} cooked today${
        data.today.avgCookMin != null ? ` · avg ${data.today.avgCookMin} min` : ""
      }${data.settings ? ` · ${data.settings.parallelTickets} at a time` : ""}`
    : "Kitchen order tickets";

  return (
    <div className="p-2 sm:p-4">
      <PageHeader
        showBackButton={role() !== "kitchen"}
        title="Kitchen"
        subtitle={subtitle}
        action={
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex rounded-lg border border-gray-300 overflow-hidden text-sm font-semibold">
              {["active", "done"].map((v) => (
                <button
                  key={v}
                  type="button"
                  onClick={() => setView(v)}
                  className={`px-3 py-1.5 ${view === v ? "bg-gray-900 text-white" : "bg-white text-gray-700"}`}
                >
                  {v === "active" ? "Active" : "Done"}
                </button>
              ))}
            </div>
            {isAdmin && (
              <button
                type="button"
                onClick={() => setShowSettings(true)}
                className="px-3 py-1.5 rounded-lg border border-gray-300 text-sm font-semibold bg-white"
              >
                ⚙ Settings
              </button>
            )}
            <button
              type="button"
              onClick={() =>
                document.fullscreenElement
                  ? document.exitFullscreen()
                  : document.documentElement.requestFullscreen?.()
              }
              className="px-3 py-1.5 rounded-lg border border-gray-300 text-sm font-semibold bg-white"
              title="Full screen"
            >
              ⛶
            </button>
          </div>
        }
      />

      {/* Source filter */}
      <div className="flex gap-2 mb-3 overflow-x-auto">
        {FILTERS.map((f) => (
          <button
            key={f.id}
            type="button"
            onClick={() => setFilter(f.id)}
            className={`px-4 py-2 rounded-full text-sm font-semibold whitespace-nowrap ${
              filter === f.id ? "bg-blue-600 text-white" : "bg-white text-gray-700 border border-gray-200"
            }`}
          >
            {f.label} <span className="opacity-75">({countFor(f.id)})</span>
          </button>
        ))}
      </div>

      {/* All dishes to cook, added up across tickets */}
      {!loading && view === "active" && (
        <KitchenSummary tickets={bySource} selectedDish={selectedDish} onSelectDish={setSelectedDish} />
      )}

      {selectedDish && view === "active" && (
        <div className="mb-3 flex items-center gap-2 text-sm">
          <span className="bg-blue-600 text-white rounded-full pl-3 pr-1 py-1 flex items-center gap-2 font-semibold">
            Tickets with {selectedDishName || "this dish"}
            <button
              type="button"
              onClick={() => setSelectedDish(null)}
              className="w-6 h-6 rounded-full bg-white/20 hover:bg-white/30"
              aria-label="Show all tickets"
            >
              ✕
            </button>
          </span>
        </div>
      )}

      {loading ? (
        <div className="text-center text-gray-500 py-16">Loading kitchen orders…</div>
      ) : view === "done" ? (
        filtered.length ? (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">{filtered.map(card)}</div>
        ) : (
          <div className="text-center text-gray-500 py-16">Nothing finished in the last 2 hours.</div>
        )
      ) : (
        <>
          {/* Phone: one column at a time */}
          <div className="flex md:hidden gap-2 mb-3">
            {COLUMNS.map((c) => (
              <button
                key={c.status}
                type="button"
                onClick={() => setMobileColumn(c.status)}
                className={`flex-1 py-2 rounded-lg text-sm font-bold ${
                  mobileColumn === c.status ? `${c.accent} text-white` : "bg-white text-gray-700 border border-gray-200"
                }`}
              >
                {c.title} ({byStatus(c.status).length})
              </button>
            ))}
          </div>

          <div className="grid md:grid-cols-3 gap-3 items-start">
            {COLUMNS.map((c) => {
              const list = byStatus(c.status);
              return (
                <section
                  key={c.status}
                  className={`${mobileColumn === c.status ? "block" : "hidden"} md:block bg-gray-100 rounded-xl p-2 min-h-[50vh]`}
                >
                  <h2 className="hidden md:flex items-center gap-2 px-1 pb-2 text-sm font-bold text-gray-800 uppercase tracking-wide">
                    <span className={`w-2.5 h-2.5 rounded-full ${c.accent}`} />
                    {c.title}
                    <span className="ml-auto bg-white rounded-full px-2 py-0.5 text-xs">{list.length}</span>
                  </h2>
                  <div className="space-y-3">
                    {list.length ? (
                      list.map(card)
                    ) : (
                      <p className="text-center text-sm text-gray-400 py-10">No tickets</p>
                    )}
                  </div>
                </section>
              );
            })}
          </div>
        </>
      )}

      {showSettings && (
        <KitchenSettings
          settings={data.settings}
          onClose={() => setShowSettings(false)}
          onSaved={(settings) => {
            setData((d) => ({ ...d, settings }));
            setShowSettings(false);
          }}
        />
      )}
    </div>
  );
};

export default KitchenPage;
